import React, { useState } from "react";
import { ChevronDown, ChevronRight, Layers, Lock, MoreHorizontal } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Phase 152 — Module + feature (sub-tab) access editor shared by Super Admin → Access Control (role-wise +
 * user-wise), Municipal Admin → Role Access (roles + custom roles) and the per-user "Menu access" dialog.
 *
 * Module levels keep the 4 steps (No access / View only / View & edit / Full access).
 * Feature levels have 3 steps (No access / View only / Full access) plus "Same as module" (inherit) — a
 * feature can never exceed its module, the badge next to it shows the effective result.
 *
 * Props
 *   modules      registry for one role: [{key,label,always_on,default,levels,help,features:[{key,label,help}]}]
 *   getValue     (key) => stored value: modules -> level | "inherit" (user mode); features -> level | "inherit"
 *   onChange     (key, value) => void
 *   mode         "role" | "custom" | "user"
 *   baseline     user mode: {key: level} role defaults / custom-role baseline (for "Inherit (X)" labels + effective)
 *   caps         user mode when a parent edits a sub-user: {key: maxLevel}
 *   disabled     boolean
 *   testId       prefix for data-testids (default "access")
 *   compact      tighter paddings (dialog)
 */
export const MODULE_LEVELS = [
  { value: "none", label: "No access" },
  { value: "view", label: "View only" },
  { value: "edit", label: "View & edit" },
  { value: "write", label: "Full access" },
];
export const FEATURE_LEVELS = [
  { value: "none", label: "No access" },
  { value: "view", label: "View only" },
  { value: "write", label: "Full access" },
];
export const RANK = { none: 0, view: 1, edit: 2, write: 3 };
export const LEVEL_BADGE = {
  none: "bg-slate-100 text-slate-600 border-slate-200",
  view: "bg-sky-50 text-sky-700 border-sky-200",
  edit: "bg-amber-50 text-amber-800 border-amber-200",
  write: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
export const levelLabel = (v) => MODULE_LEVELS.find((l) => l.value === v)?.label || "Full access";
const minLevel = (a, b) => (RANK[a] <= RANK[b] ? a : b);
const fk = (m, f) => `${m}.${f}`;

const LevelBadge = ({ level, testId }) => (
  <Badge variant="outline" className={`text-[10px] whitespace-nowrap ${LEVEL_BADGE[level] || LEVEL_BADGE.write}`} data-testid={testId}>
    {levelLabel(level)}
  </Badge>
);

export const ModuleAccessEditor = ({ modules = [], getValue, onChange, mode = "role", baseline = {}, caps = null,
  disabled = false, testId = "access", compact = false }) => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (k) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  // Effective MODULE level for a module key (resolves "inherit" in user mode).
  const moduleEffective = (m) => {
    if (m.always_on) return "write";
    const v = getValue(m.key);
    if (v && v !== "inherit") return v;
    if (mode === "user") return baseline[m.key] || m.default || "write";
    if (mode === "custom") return "none";
    return m.default || "write";
  };
  // Effective FEATURE level: configured (capped) or inherit (module level / user baseline).
  // Phase 177: a feature may declare its own registry `default` (e.g. Delete receipts = No access) used when it is not configured.
  const featureInherit = (m, f, modLevel) => minLevel(f.default || modLevel, modLevel);
  const featureEffective = (m, f, modLevel) => {
    const key = fk(m.key, f.key);
    const v = getValue(key);
    if (v && v !== "inherit") return minLevel(v === "edit" ? "write" : v, modLevel);
    if (mode === "user") return minLevel(baseline[key] || featureInherit(m, f, modLevel), modLevel);
    return featureInherit(m, f, modLevel);
  };

  const setAllFeatures = (m, value) => (m.features || []).forEach((f) => onChange(fk(m.key, f.key), value));

  const rowPad = compact ? "px-2.5 py-1.5" : "px-3 py-2";
  // level pickers: user mode shows the longer "Inherit (Full access)" labels, so it gets a wider trigger
  const selW = mode === "user" ? "w-44" : compact ? "w-36" : "w-40";

  return (
    <div className={compact ? "space-y-1.5" : "space-y-2"} data-testid={`${testId}-editor`}>
      {modules.map((m) => {
        const feats = m.features || [];
        const modLevel = moduleEffective(m);
        const isOpen = open.has(m.key);
        const customised = feats.filter((f) => { const v = getValue(fk(m.key, f.key)); return v && v !== "inherit"; }).length;
        const modCap = caps ? (caps[m.key] || "write") : null;
        const modValue = m.always_on ? "write" : (getValue(m.key) || (mode === "user" ? "inherit" : mode === "custom" ? "none" : (m.default || "write")));
        return (
          <div key={m.key} className={`border rounded-lg ${isOpen ? "bg-muted/20" : ""}`} data-testid={`${testId}-module-${m.key}`}>
            {/* ---- module row ---- */}
            <div className={`flex items-center justify-between gap-3 ${rowPad}`}>
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                {feats.length > 0 ? (
                  <button type="button" onClick={() => toggle(m.key)} aria-expanded={isOpen}
                    className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
                    title={isOpen ? "Hide sub-features" : `Show ${feats.length} sub-features`} data-testid={`${testId}-toggle-${m.key}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                ) : <span className="h-6 w-6 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium flex flex-wrap items-center gap-x-1.5 gap-y-0.5 break-words leading-snug" title={m.label}>
                    <span>{m.label}</span>
                    {m.always_on && <Badge variant="outline" className="text-[10px] shrink-0"><Lock className="h-2.5 w-2.5 mr-1" />always on</Badge>}
                    {feats.length > 0 && (
                      <button type="button" onClick={() => toggle(m.key)}
                        className={`text-[10px] px-1.5 py-px rounded-full border inline-flex items-center gap-1 whitespace-nowrap ${customised ? "bg-primary/10 text-primary border-primary/20" : "text-muted-foreground border-border"}`}
                        data-testid={`${testId}-feature-count-${m.key}`}>
                        <Layers className="h-2.5 w-2.5" /> {feats.length} sub-features{customised ? ` · ${customised} customised` : ""}
                      </button>
                    )}
                  </div>
                  {mode === "user" && !m.always_on && (
                    <p className="text-[10px] text-muted-foreground">Role default: {levelLabel(baseline[m.key] || m.default || "write")}</p>
                  )}
                  {m.help && <p className="text-[11px] text-muted-foreground leading-snug" data-testid={`${testId}-help-${m.key}`}>{m.help}</p>}
                </div>
              </div>
              {m.always_on ? (
                <LevelBadge level="write" />
              ) : (
                <Select value={modValue} onValueChange={(v) => onChange(m.key, v)} disabled={disabled}>
                  <SelectTrigger className={`h-8 ${selW} text-xs shrink-0`} data-testid={`${testId}-level-${m.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {mode === "user" && <SelectItem value="inherit">Inherit ({levelLabel(baseline[m.key] || m.default || "write")})</SelectItem>}
                    {MODULE_LEVELS.filter((l) => !m.levels || m.levels.includes(l.value)).map((l) => (
                      <SelectItem key={l.value} value={l.value} disabled={modCap ? RANK[l.value] > RANK[modCap] : false}>
                        {l.label}{modCap && RANK[l.value] > RANK[modCap] ? " (above your level)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* ---- feature rows ---- */}
            {isOpen && feats.length > 0 && (
              <div className={`border-t ${compact ? "px-2.5 py-1.5" : "px-3 py-2"} space-y-1`} data-testid={`${testId}-features-${m.key}`}>
                <div className="flex items-center justify-between gap-2 pl-7 pb-1">
                  <p className="text-[11px] text-muted-foreground">
                    Sub-features follow the module ({levelLabel(modLevel)}) unless set here — a sub-feature can never exceed its module.
                  </p>
                  {!disabled && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" data-testid={`${testId}-set-all-${m.key}`}>
                          <MoreHorizontal className="h-3.5 w-3.5 mr-1" /> Set all
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuLabel className="text-xs">All sub-features of {m.label}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setAllFeatures(m, "inherit")} data-testid={`${testId}-set-all-${m.key}-inherit`}>Same as module</DropdownMenuItem>
                        {FEATURE_LEVELS.map((l) => (
                          <DropdownMenuItem key={l.value} onClick={() => setAllFeatures(m, l.value)} data-testid={`${testId}-set-all-${m.key}-${l.value}`}>{l.label}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                {feats.map((f) => {
                  const key = fk(m.key, f.key);
                  const raw = getValue(key) || "inherit";
                  const eff = featureEffective(m, f, modLevel);
                  const capped = raw !== "inherit" && RANK[raw === "edit" ? "write" : raw] > RANK[modLevel];
                  const fCap = caps ? (caps[key] || caps[m.key] || "write") : null;
                  const inheritLabel = mode === "user" ? levelLabel(minLevel(baseline[key] || featureInherit(m, f, modLevel), modLevel)) : levelLabel(featureInherit(m, f, modLevel));
                  return (
                    <div key={f.key}
                      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md pl-7 pr-1 py-1 hover:bg-muted/50 ${raw !== "inherit" ? "bg-primary/[0.04]" : ""}`}
                      data-testid={`${testId}-feature-${m.key}-${f.key}`}>
                      {/* name column grows and may wrap; when the card is narrow the controls drop to their own line below the full name */}
                      <div className="min-w-0 flex-1 basis-44" title={f.label}>
                        <p className="text-xs leading-snug break-words">{f.label}</p>
                        {f.help && <p className="text-[10px] text-muted-foreground leading-snug">{f.help}</p>}
                        {f.default && <p className="text-[10px] text-amber-700" data-testid={`${testId}-feature-default-${m.key}-${f.key}`}>Default: {levelLabel(f.default)} — not inherited from the module</p>}
                        {capped && <p className="text-[10px] text-amber-700">Capped by the module — effective: {levelLabel(eff)}</p>}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                        <LevelBadge level={eff} testId={`${testId}-feature-effective-${m.key}-${f.key}`} />
                        <Select value={raw} onValueChange={(v) => onChange(key, v)} disabled={disabled}>
                          <SelectTrigger className={`h-7 ${selW} text-[11px]`} data-testid={`${testId}-feature-level-${m.key}-${f.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">{mode === "user" ? `Inherit (${inheritLabel})` : f.default ? `Default (${inheritLabel})` : "Same as module"}</SelectItem>
                            {FEATURE_LEVELS.map((l) => (
                              <SelectItem key={l.value} value={l.value} disabled={fCap ? RANK[l.value] > RANK[fCap] : false}>
                                {l.label}{fCap && RANK[l.value] > RANK[fCap] ? " (above your level)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Counts configured feature keys ("module.feature") in a stored map — for summaries. */
export const countConfiguredFeatures = (map) => Object.keys(map || {}).filter((k) => k.includes(".") && map[k] && map[k] !== "inherit").length;

export default ModuleAccessEditor;
